// Phase 7 Slice F — Stripe Identity widget.
//
// Replaces the static KycFormSkeleton at registry key 'kyc-form'.
// Cell templates referencing :::widget[kyc-form]:::  continue to work.
//
// Flow:
//   1. CTA click → POST /kyc/create-session → { client_secret }
//   2. Lazy-import @stripe/stripe-js (saves ~16KB on cells without
//      this widget)
//   3. stripe.verifyIdentity(client_secret) → opens Stripe iframe modal
//   4. modal closes → flat "officer will review shortly" message
//
// Out of scope (Slice G if UX warrants):
//   - SSE-driven update when kyc.officer_attested lands at projector
//   - retry-with-backoff if session-create transiently fails

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { WidgetProps } from './registry';
import { Button } from '@rmint/ui';
import { env } from '@rmint/shell';
import { useSubscriptionState } from '../../lib/use-subscription-state';

type WidgetState = 'idle' | 'creating' | 'modal' | 'done' | 'verified' | 'error';

function ctaLabel(state: WidgetState): string {
  switch (state) {
    case 'idle': return 'Verify identity';
    case 'creating': return 'Opening Stripe…';
    case 'modal': return 'Verifying…';
    case 'done': return 'Verification submitted';
    case 'verified': return 'Continue to allocate →';
    case 'error': return 'Try again';
  }
}

// Phases past kyc-submitted that should render as 'verified'. Includes
// the transient officer-attested phase so the widget shows verified
// immediately on the post-Stripe webhook landing, before kyc.verified.
const VERIFIED_PHASES = new Set([
  '12-kyc-officer-attested',
  '03-kyc-verified',
  '04-allocated',
  '05-signed',
  '06-purchased',
  '07-holding',
  '08-settled',
]);

export function KycStripeIdentity(props: WidgetProps) {
  const [state, setState] = useState<WidgetState>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const navigate = useNavigate();

  // HTML attributes get lowercased by the rehype pipeline, so accept
  // either the camelCase prop (from React-direct call sites + the renderer's
  // normalizeAttrCase pass) or the lowercase HTML-attribute fallback.
  const subscriptionId =
    (props.subscriptionId as string | undefined) ??
    (props.subscriptionid as string | undefined);

  const onComplete = props.onComplete as ((text: string) => void) | undefined;

  // D.2.16: read the subscription's dossier state on mount. If the
  // subscription has already advanced past 02-kyc-submitted we render
  // the verified branch instead of the idle upload prompt. Failure to
  // fetch falls back to idle silently — never blocks first-time users.
  const sub = useSubscriptionState(subscriptionId);
  const dossierPhase = sub.state?.currentPhase;
  const isVerifiedFromDossier =
    dossierPhase !== undefined && VERIFIED_PHASES.has(dossierPhase);
  // Debug override (used by unit tests + /debug/widgets): if `state` is
  // passed in as a directive prop, it wins over the hook + dossier path.
  // Mirrors the pattern used in subscribe-allocate-form / sign-form / fund-flow.
  const debugStateOverride = props.state as WidgetState | undefined;
  const effectiveState: WidgetState =
    debugStateOverride ?? (isVerifiedFromDossier ? 'verified' : state);

  async function handleClick() {
    // verified branch's primary CTA is "Continue to allocate".
    // Chat context (agent-driven flow): dispatch via onComplete so the
    // agent can advance the conversation.
    // Subscribe-page context (no agent): navigate directly to the next
    // step — matches the pattern used by sibling widgets
    // (subscribe-allocate-form:211, subscribe-sign-form:185, etc.).
    if (effectiveState === 'verified') {
      if (onComplete) {
        onComplete('Continue to allocation');
      } else if (subscriptionId) {
        navigate(`/subscribe/${encodeURIComponent(subscriptionId)}/allocate`);
      } else {
        console.debug('[kyc-form] no onComplete + no subscriptionId; verified-CTA is a no-op');
      }
      return;
    }
    if (!env.stripePublishableKey) {
      setErrorMsg('Stripe publishable key not configured');
      setState('error');
      return;
    }
    if (!subscriptionId) {
      setErrorMsg('Missing subscriptionId on widget');
      setState('error');
      return;
    }

    setState('creating');
    setErrorMsg('');
    try {
      const res = await fetch('/kyc/create-session', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ subscriptionId }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: { kind?: string } };
        throw new Error(`session create failed (${res.status}): ${body.error?.kind ?? 'unknown'}`);
      }
      const { client_secret } = (await res.json()) as { client_secret: string };

      setState('modal');
      const { loadStripe } = await import('@stripe/stripe-js');
      const stripe = await loadStripe(env.stripePublishableKey);
      if (!stripe) throw new Error('stripe.js failed to load');

      const result = await stripe.verifyIdentity(client_secret);
      if (result.error) {
        throw new Error(result.error.message ?? 'verification cancelled');
      }
      setState('done');
    } catch (e) {
      setErrorMsg((e as Error).message);
      setState('error');
    }
  }

  const intent =
    effectiveState === 'verified' || effectiveState === 'done'
      ? 'success'
      : effectiveState === 'error'
        ? 'warning'
        : 'action';
  const tone = {
    info: 'border-border bg-card',
    action: 'border-primary/30 bg-primary/5',
    success: 'border-success-line bg-success-bg',
    warning: 'border-warn-line bg-warn-bg',
  }[intent];

  // Decision data shown on the verified branch — sourced from dossier.
  // raw.data is Record<string, unknown>; the cast describes our expected
  // shape, but a future projector emit could ship validUntil as a Date
  // or number, so the render path guards each field by `typeof` rather
  // than trusting the cast.
  const dossierVerifiedData = sub.state?.raw.data as
    | { decisionId?: string; validUntil?: string; officerId?: string; riskScore?: string }
    | undefined;
  // Tests pass synthetic decision metadata via debugVerifiedData when
  // the state debug override is in effect (no dossier read).
  const debugVerifiedData = props.debugVerifiedData as
    | { decisionId?: string; validUntil?: string; officerId?: string; riskScore?: string }
    | undefined;
  const verifiedData = debugVerifiedData ?? dossierVerifiedData;
  const validUntilStr =
    typeof verifiedData?.validUntil === 'string' ? verifiedData.validUntil : undefined;

  return (
    <section className={`my-4 rounded-xl border p-5 ${tone}`}>
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h3 className="text-base font-semibold text-foreground">Identity verification</h3>
        <span className="text-[10px] uppercase tracking-wider text-foreground-mute">widget</span>
      </div>

      <div className="space-y-2 text-sm text-foreground/90">
        {effectiveState === 'verified' && (
          <div className="space-y-2">
            <p className="flex items-center gap-2 text-base font-semibold text-success">
              <span aria-hidden className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-success-bg border-2 border-success">✓</span>
              <output className="font-semibold">Verified</output>
            </p>
            {(verifiedData?.riskScore != null || validUntilStr || verifiedData?.officerId) && (
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-foreground-mute">
                {verifiedData?.riskScore != null ? (
                  <>
                    <dt>Decision</dt>
                    <dd className="m-0">{verifiedData.riskScore}-risk</dd>
                  </>
                ) : null}
                {validUntilStr ? (
                  <>
                    <dt>Valid until</dt>
                    <dd className="m-0">{validUntilStr.slice(0, 10)}</dd>
                  </>
                ) : null}
                {verifiedData?.officerId ? (
                  <>
                    <dt>Reviewed by</dt>
                    <dd className="m-0">{verifiedData.officerId}</dd>
                  </>
                ) : null}
              </dl>
            )}
          </div>
        )}
        {effectiveState === 'idle' && (
          <p>Upload your government ID + selfie via Stripe. Stripe verifies and routes the result to a compliance officer for review.</p>
        )}
        {effectiveState === 'creating' && <p>Opening Stripe Identity…</p>}
        {effectiveState === 'modal' && <p>Stripe is verifying your documents.</p>}
        {effectiveState === 'done' && <p>Stripe accepted your documents. An officer will review shortly.</p>}
        {effectiveState === 'error' && (
          <p className="text-warn">Something went wrong: <code className="text-xs">{errorMsg}</code></p>
        )}
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          onClick={handleClick}
          disabled={
            sub.loading ||
            effectiveState === 'creating' ||
            effectiveState === 'modal' ||
            effectiveState === 'done'
          }
        >
          {sub.loading ? 'Checking status…' : ctaLabel(effectiveState)}
        </Button>
      </div>

      <p className="mt-4 pt-3 border-t border-border/60 text-[11px] text-foreground-mute">
        {effectiveState === 'verified'
          ? 'KYC complete · Phase 7 Slice F.'
          : effectiveState === 'done'
            ? 'Officer review is asynchronous; you can close this page.'
            : 'Stripe Identity (test mode) — Phase 7 Slice F.'}
      </p>
    </section>
  );
}
